/**
 * Draggable panel: `react-draggable` — small, focused on free-form drag + bounds.
 * We use `bounds="parent"` on a fixed full-viewport layer portaled to `document.body`, not `bounds="body"`.
 * The latter mixes `offsetLeft`/`offsetTop` with `transform` and mis-handles `position:fixed`, causing jumps
 * and wrong horizontal limits.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import Draggable from 'react-draggable';
import { Box, IconButton } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PolylineOutlinedIcon from '@mui/icons-material/PolylineOutlined';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useAuth } from '../authContext';
import { useProject } from '../ProjectContext';
import FormsAPI from '../FormsAPI';
import ConfirmationDialog from '../ConfirmationDialog';
import { VISIBILITY, SHAPE_TYPE, DEFAULT_PROJECT_FORM_ID } from '../utils/constants';
import { WIZARD_ACTIONS } from './constants';
import WizardProgressBar from './WizardProgressBar';
import useWizardState from './hooks/useWizardState';
import useWizardSubmit from './hooks/useWizardSubmit';
import useFilesByField from './hooks/useFilesByField';
import LocationStep from './steps/LocationStep';
import DateStep from './steps/DateStep';
import NameStep from './steps/NameStep';
import StoryStep from './steps/StoryStep';
import AdditionalInfoStep from './steps/AdditionalInfoStep';
import ReviewStep from './steps/ReviewStep';
import './WizardContribution.css';

function getWizardDefaultPosition() {
  if (typeof window === 'undefined') return { x: 24, y: 96 };
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const margin = 16;
  // Breathing room from the right edge, and enough top clearance to avoid
  // overlapping the timeline slider at the top of the map. Right margin is
  // generous enough to visually detach the panel from the viewport edge so
  // the left edge roughly lines up with the 2000s tick on the timeline at
  // common resolutions.
  const rightMargin = 180;
  const topClearance = 96;
  const panelW = Math.min(480, vw - margin * 2);
  const estH = Math.min(vh * 0.78, 620);
  if (vw <= 600) {
    return {
      x: 0,
      y: Math.max(margin, vh - estH - margin),
    };
  }
  return {
    x: Math.max(margin, vw - rightMargin - panelW),
    y: Math.max(topClearance, (vh - estH) / 2),
  };
}

class WizardErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(err, info) {
    console.error('Wizard error:', err, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <p>Something went wrong. Please close and try again.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function getDefaultVisibility(project, user) {
  const siteType = project?.site_type;
  if (siteType === 'exhibition') return VISIBILITY.PROJECT;
  if (siteType === 'city') return VISIBILITY.PUBLIC;
  return user ? VISIBILITY.PRIVATE : VISIBILITY.PROJECT;
}

const WizardContribution = ({
  onClose,
  onSwitchToLegacy,
  selectedAddress,
  setSelectedAddress,
  selectedAddressLatLng,
  setSelectedAddressLatLng,
  setSelectMode,
  selectMode,
  year,
  setYear,
  refreshData,
  locations,
  isExhibitionMode,
  mapRef,
  activatePolygonDrawing,
  isDrawingPolygon,
  polygonCoordinates,
  setPolygonCoordinates,
  activateLineDrawing,
  isDrawingLine,
  lineCoordinates,
  setLineCoordinates,
  setIsDrawingLine,
  setSelectionType,
  setIsDrawingPolygon,
  setDrawingPoints,
  setActiveTool,
  onSubmissionSuccess,
}) => {
  const { user } = useAuth();
  const { project } = useProject();

  const defaultVis = getDefaultVisibility(project, user);
  const { state, dispatch, isStepValid } = useWizardState(defaultVis, { user, isExhibitionMode });
  const multiFile = useFilesByField();
  const { submit, progress, error, isSubmitting, setError } = useWizardSubmit({
    project,
    user,
    isExhibitionMode,
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [schemaStatus, setSchemaStatus] = useState('loading');
  const schemaRequestRef = useRef(0);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [drawToolsOpen, setDrawToolsOpen] = useState(false);
  const [wizardDragging, setWizardDragging] = useState(false);
  const [wizardDefaultPosition] = useState(getWizardDefaultPosition);
  const wizardDragRef = useRef(null);
  const wizardInitialFocusRef = useRef(null);
  const schemaRetryButtonRef = useRef(null);
  const [restoreSchemaFocus, setRestoreSchemaFocus] = useState(false);
  // ISSUE-004: capture the enriched submit response (shape_type, shape_id,
  // contribution_ids, approval_status, visibility) so handleConfirmationClose
  // can hand the (shape_type, shape_id) pair to the parent. Stored as a ref
  // because we don't need to re-render between submit-success and close —
  // the dialog is already controlled by `confirmationOpen`.
  const lastSubmissionRef = useRef(null);

  const isPolygonMode = useMemo(() => state.shapeType === SHAPE_TYPE.POLYGON, [state.shapeType]);
  const isLineMode = useMemo(() => state.shapeType === SHAPE_TYPE.LINE, [state.shapeType]);

  useEffect(() => {
    if (currentStep !== 1) setDrawToolsOpen(false);
  }, [currentStep]);

  useEffect(() => {
    wizardInitialFocusRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    if (!restoreSchemaFocus || schemaStatus === 'loading') return;
    setRestoreSchemaFocus(false);

    if (schemaStatus === 'error' || schemaStatus === 'empty') {
      schemaRetryButtonRef.current?.focus({ preventScroll: true });
      return;
    }

    const readyControl = wizardDragRef.current?.querySelector(
      '.wizard__step input:not([disabled]), .wizard__step textarea:not([disabled]), .wizard__step select:not([disabled]), .wizard__step button:not([disabled])',
    );
    (readyControl || wizardDragRef.current)?.focus({ preventScroll: true });
  }, [schemaStatus, restoreSchemaFocus]);

  // Explicitly clear map-select mode on Step 1 entry in marker mode.
  // The user must tap "Click a spot on the map" to arm map-click address
  // pickup. Auto-arming confuses users into thinking the button is dead
  // (they click it, it toggles off, their next map click no-ops).
  useEffect(() => {
    if (currentStep === 1 && !isPolygonMode && !isLineMode && setSelectMode) {
      setSelectMode(false);
    }
  }, [currentStep, isPolygonMode, isLineMode, setSelectMode]);

  const loadContributionSchema = useCallback(async () => {
    const projectId = project?.projectsid;
    if (!projectId) {
      setSchemaStatus('error');
      return;
    }

    const requestId = ++schemaRequestRef.current;
    setSchemaStatus('loading');
    // A project change or retry must never leave a stale selected form usable.
    dispatch({ type: WIZARD_ACTIONS.SET_FORM_ID, payload: null });
    dispatch({ type: WIZARD_ACTIONS.SET_FORM_FIELDS, payload: [] });

    try {
      const response = await FormsAPI.getFormsProject(projectId);
      if (requestId !== schemaRequestRef.current) return;
      const forms = Array.isArray(response?.data?.forms) ? response.data.forms : [];
      if (forms.length === 0) {
        setSchemaStatus('empty');
        return;
      }
      const form = forms.length === 1
        ? forms[0]
        : forms.find((candidate) => candidate.id === DEFAULT_PROJECT_FORM_ID) || forms[0];
      if (!form || !Array.isArray(form.fields)) {
        setSchemaStatus('error');
        return;
      }
      dispatch({ type: WIZARD_ACTIONS.SET_FORM_ID, payload: form.id });
      dispatch({ type: WIZARD_ACTIONS.SET_FORM_FIELDS, payload: form.fields });
      setSchemaStatus('ready');
    } catch (_error) {
      if (requestId === schemaRequestRef.current) setSchemaStatus('error');
    }
  }, [project?.projectsid, dispatch]);

  useEffect(() => {
    loadContributionSchema();
    return () => {
      schemaRequestRef.current += 1;
    };
  }, [loadContributionSchema]);

  const retryContributionSchema = useCallback(() => {
    setRestoreSchemaFocus(true);
    loadContributionSchema();
  }, [loadContributionSchema]);

  // Sync incoming address/latLng from map clicks
  useEffect(() => {
    if (selectedAddress) {
      dispatch({ type: WIZARD_ACTIONS.SET_ADDRESS, payload: selectedAddress });
    }
  }, [selectedAddress, dispatch]);

  useEffect(() => {
    if (selectedAddressLatLng) {
      dispatch({ type: WIZARD_ACTIONS.SET_LATLNG, payload: selectedAddressLatLng });
    }
  }, [selectedAddressLatLng, dispatch]);

  // Sync polygon coordinates from map into wizard state
  useEffect(() => {
    if (polygonCoordinates && polygonCoordinates.length >= 3) {
      dispatch({ type: WIZARD_ACTIONS.SET_POLYGON_COORDINATES, payload: polygonCoordinates });
    }
  }, [polygonCoordinates, dispatch]);

  // Sync line coordinates from map into wizard state
  useEffect(() => {
    if (lineCoordinates && lineCoordinates.length >= 2) {
      dispatch({ type: WIZARD_ACTIONS.SET_LINE_COORDINATES, payload: lineCoordinates });
    }
  }, [lineCoordinates, dispatch]);

  // Sync year with parent
  useEffect(() => {
    if (year) {
      dispatch({ type: WIZARD_ACTIONS.SET_YEAR, payload: year });
    }
  }, [year, dispatch]);

  const goNext = useCallback(() => {
    if (schemaStatus === 'ready' && currentStep < 6 && isStepValid(currentStep)) {
      // Sync year back to parent when leaving step 2
      if (currentStep === 2 && setYear) {
        setYear(state.year);
      }
      // Sync address/latLng back to parent when leaving step 1
      if (currentStep === 1) {
        if (setSelectedAddress && state.address) {
          setSelectedAddress(state.address);
        }
        if (setSelectedAddressLatLng && state.latLng) {
          setSelectedAddressLatLng(state.latLng);
        }
      }
      setCurrentStep((s) => s + 1);
    }
  }, [schemaStatus, currentStep, isStepValid, state, setYear, setSelectedAddress, setSelectedAddressLatLng]);

  const goBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep((s) => s - 1);
    }
  }, [currentStep]);

  const goToStep = useCallback(
    (step) => {
      if (step < currentStep) setCurrentStep(step);
    },
    [currentStep],
  );

  const handleMapClick = useCallback(() => {
    // Force selectMode on instead of toggling. Step 1 auto-enables
    // selectMode on mount, so a toggle-on-click would turn it OFF on
    // the user's first tap of the "Click a spot on the map" button,
    // making the next map click a no-op (feedback 2026-04-15 #2).
    if (setSelectMode) setSelectMode(true);
  }, [setSelectMode]);

  const handleActivatePolygon = useCallback(() => {
    dispatch({ type: WIZARD_ACTIONS.SET_SHAPE_TYPE, payload: SHAPE_TYPE.POLYGON });
    dispatch({ type: WIZARD_ACTIONS.SET_LATLNG, payload: null });
    dispatch({ type: WIZARD_ACTIONS.SET_ADDRESS, payload: '' });
    dispatch({ type: WIZARD_ACTIONS.SET_LINE_COORDINATES, payload: [] });
    if (setLineCoordinates) setLineCoordinates([]);
    if (setIsDrawingLine) setIsDrawingLine(false);
    if (setSelectedAddressLatLng) setSelectedAddressLatLng(null);
    if (setSelectMode) setSelectMode(false);
    if (activatePolygonDrawing) activatePolygonDrawing();
  }, [dispatch, activatePolygonDrawing, setSelectedAddressLatLng, setSelectMode, setLineCoordinates, setIsDrawingLine]);

  const handleActivateLine = useCallback(() => {
    dispatch({ type: WIZARD_ACTIONS.SET_SHAPE_TYPE, payload: SHAPE_TYPE.LINE });
    dispatch({ type: WIZARD_ACTIONS.SET_LATLNG, payload: null });
    dispatch({ type: WIZARD_ACTIONS.SET_ADDRESS, payload: '' });
    dispatch({ type: WIZARD_ACTIONS.SET_POLYGON_COORDINATES, payload: [] });
    if (setPolygonCoordinates) setPolygonCoordinates([]);
    if (setIsDrawingPolygon) setIsDrawingPolygon(false);
    if (setSelectedAddressLatLng) setSelectedAddressLatLng(null);
    if (setSelectMode) setSelectMode(false);
    if (activateLineDrawing) activateLineDrawing();
  }, [dispatch, activateLineDrawing, setSelectedAddressLatLng, setSelectMode, setPolygonCoordinates, setIsDrawingPolygon]);

  const handleActivateMarker = useCallback(() => {
    dispatch({ type: WIZARD_ACTIONS.SET_SHAPE_TYPE, payload: SHAPE_TYPE.MARKER });
    dispatch({ type: WIZARD_ACTIONS.SET_POLYGON_COORDINATES, payload: [] });
    dispatch({ type: WIZARD_ACTIONS.SET_LINE_COORDINATES, payload: [] });
    if (setIsDrawingPolygon) setIsDrawingPolygon(false);
    if (setIsDrawingLine) setIsDrawingLine(false);
    if (setDrawingPoints) setDrawingPoints([]);
    if (setPolygonCoordinates) setPolygonCoordinates([]);
    if (setLineCoordinates) setLineCoordinates([]);
    if (setSelectionType) setSelectionType(SHAPE_TYPE.MARKER);
    if (setActiveTool) setActiveTool('point');
  }, [dispatch, setIsDrawingPolygon, setIsDrawingLine, setDrawingPoints, setPolygonCoordinates, setLineCoordinates, setSelectionType, setActiveTool]);

  const handleRedrawPolygon = useCallback(() => {
    dispatch({ type: WIZARD_ACTIONS.SET_POLYGON_COORDINATES, payload: [] });
    if (setPolygonCoordinates) setPolygonCoordinates([]);
    if (activatePolygonDrawing) activatePolygonDrawing();
  }, [dispatch, setPolygonCoordinates, activatePolygonDrawing]);

  const handleRedrawLine = useCallback(() => {
    dispatch({ type: WIZARD_ACTIONS.SET_LINE_COORDINATES, payload: [] });
    if (setLineCoordinates) setLineCoordinates([]);
    if (activateLineDrawing) activateLineDrawing();
  }, [dispatch, setLineCoordinates, activateLineDrawing]);

  const handleSubmit = useCallback(async () => {
    if (schemaStatus !== 'ready' || !state.formId || !Array.isArray(state.formFields)) {
      setError('The contribution form is not ready. Please retry loading it.');
      return;
    }
    const hasStory = Boolean(state.storyText?.trim());
    const hasFiles = multiFile.totalCount > 0;
    if (!hasStory && !hasFiles) {
      setError('Please add a story and/or at least one file before submitting.');
      return;
    }
    try {
      const result = await submit({
        wizardState: state,
        filesByField: multiFile.buckets,
        selectionType: state.shapeType || 'marker',
      });
      // submitDynDataNU.php now returns shape_type + shape_id (ISSUE-004) so
      // we can tell the parent which shape to focus the side panel on after
      // the user dismisses the confirmation dialog. Defensive defaults in
      // case the backend ever drops the enrichment.
      lastSubmissionRef.current = {
        shapeType: result?.shape_type || state.shapeType || 'marker',
        shapeId: result?.shape_id ?? null,
      };

      let msg;
      if (state.visibility === VISIBILITY.PRIVATE) {
        msg = 'Thanks for your contribution.';
      } else if (project?.site_type === 'exhibition') {
        msg = 'Thanks for your contribution. Your content will be reviewed and if approved will be posted within the next 48 hours.';
      } else {
        const label = state.visibility === VISIBILITY.PROJECT
          ? (project?.project_name || 'Project Map')
          : 'Public';
        msg = `Thanks for your contribution. Your content will be reviewed soon for ${label} display approval.`;
      }
      setConfirmationMessage(msg);
      setConfirmationOpen(true);
    } catch {
      // error already set by the hook
    }
  }, [schemaStatus, submit, state, multiFile.buckets, multiFile.totalCount, project, setError]);

  const handleConfirmationClose = useCallback(() => {
    setConfirmationOpen(false);
    dispatch({ type: WIZARD_ACTIONS.RESET, payload: { defaultVisibility: defaultVis } });
    multiFile.resetFiles();
    setCurrentStep(1);
    if (setIsDrawingPolygon) setIsDrawingPolygon(false);
    if (setDrawingPoints) setDrawingPoints([]);
    if (setPolygonCoordinates) setPolygonCoordinates([]);
    if (setSelectionType) setSelectionType(SHAPE_TYPE.MARKER);
    if (setActiveTool) setActiveTool('point');
    if (refreshData) refreshData();
    if (onClose) onClose();
    // ISSUE-004: pop the side panel onto the new shape AFTER the wizard has
    // closed so the panel doesn't fight the wizard for the same screen real
    // estate. refreshData() above is fire-and-forget, but SidePanelComponent
    // tolerates a not-yet-refreshed `polygons` array (it falls back to the
    // raw shape id) so the focus call is safe to fire immediately.
    const submission = lastSubmissionRef.current;
    if (submission?.shapeId != null && onSubmissionSuccess) {
      onSubmissionSuccess(submission);
    }
    lastSubmissionRef.current = null;
  }, [dispatch, defaultVis, multiFile, refreshData, onClose, onSubmissionSuccess,
      setIsDrawingPolygon, setDrawingPoints, setPolygonCoordinates, setSelectionType, setActiveTool]);

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <LocationStep
            state={state}
            dispatch={dispatch}
            onNext={goNext}
            onMapClick={handleMapClick}
            isMapSelectActive={selectMode}
            onSwitchToLegacy={onSwitchToLegacy}
            locations={locations}
            mapRef={mapRef}
            setSelectMode={setSelectMode}
            setSelectedAddressLatLng={setSelectedAddressLatLng}
            setSelectedAddress={setSelectedAddress}
            onActivatePolygon={handleActivatePolygon}
            onActivateMarker={handleActivateMarker}
            onActivateLine={handleActivateLine}
            onRedrawPolygon={handleRedrawPolygon}
            onRedrawLine={handleRedrawLine}
            isDrawingPolygon={isDrawingPolygon}
            isDrawingLine={isDrawingLine}
            schemaReady={schemaStatus === 'ready'}
          />
        );
      case 2:
        return (
          <DateStep
            state={state}
            dispatch={dispatch}
            onNext={goNext}
            onBack={goBack}
          />
        );
      case 3:
        return (
          <NameStep
            state={state}
            dispatch={dispatch}
            onNext={goNext}
            onBack={goBack}
          />
        );
      case 4:
        return (
          <StoryStep
            state={state}
            dispatch={dispatch}
            multiFile={multiFile}
            onNext={goNext}
            onBack={goBack}
          />
        );
      case 5:
        return (
          <AdditionalInfoStep
            state={state}
            dispatch={dispatch}
            onNext={goNext}
            onBack={goBack}
          />
        );
      case 6:
        return (
          <ReviewStep
            state={state}
            dispatch={dispatch}
            fileCount={multiFile.totalCount}
            files={multiFile.allFilesFlat}
            onBack={goBack}
            onSubmit={handleSubmit}
            progress={progress}
            isSubmitting={isSubmitting}
            error={error}
            isExhibitionMode={isExhibitionMode}
          />
        );
      default:
        return null;
    }
  };

  const wizardTree = (
    <div className="wizard-drag-bounds">
      <Draggable
        nodeRef={wizardDragRef}
        handle=".wizard__header"
        cancel=".wizard__header-close"
        defaultPosition={wizardDefaultPosition}
        bounds="parent"
        onStart={() => setWizardDragging(true)}
        onStop={() => setWizardDragging(false)}
      >
        <div
          ref={wizardDragRef}
          className={'wizard' + (wizardDragging ? ' wizard--dragging' : '')}
          role="dialog"
          aria-modal="false"
          aria-labelledby="wizard-contribution-title"
          tabIndex={-1}
        >
        {currentStep === 1 && (
          <Box
            className="wizard__draw-tools-mount"
            sx={{
              position: 'absolute',
              left: 0,
              top: '120px',
              transform: 'translateX(-100%)',
              zIndex: 1101,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
            }}
          >
            <Box
              onClick={() => setDrawToolsOpen((prev) => !prev)}
              sx={{
                writingMode: 'vertical-rl',
                textOrientation: 'mixed',
                transform: 'rotate(180deg)',
                cursor: 'pointer',
                padding: '12px 6px',
                backgroundColor: 'var(--theme-bg-header)',
                color: 'var(--theme-color)',
                fontWeight: 700,
                fontSize: '0.8rem',
                letterSpacing: '0.05em',
                borderRadius: '6px 6px 0 0',
                userSelect: 'none',
                border: 'none',
                outline: 'none',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: 'var(--theme-bg-clr)',
                },
              }}
            >
              Draw Tools
            </Box>
            {drawToolsOpen && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  padding: '6px',
                  backgroundColor: 'rgba(245,245,245,0.95)',
                  borderRadius: '6px 0 0 6px',
                  boxShadow: '-2px 2px 6px rgba(0,0,0,0.15)',
                }}
              >
                <Tooltip title="Point" placement="left">
                  <IconButton
                    size="small"
                    onClick={() => {
                      handleActivateMarker();
                      setDrawToolsOpen(false);
                    }}
                    sx={{
                      color: 'var(--theme-color)',
                      backgroundColor: !isPolygonMode ? 'var(--theme-bg-clr)' : '#fff',
                      border: !isPolygonMode ? '2px solid var(--theme-color)' : '1px solid transparent',
                      borderRadius: '6px',
                      '&:hover': { backgroundColor: 'var(--theme-bg-clr)' },
                    }}
                  >
                    <LocationOnIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Polygon" placement="left">
                  <IconButton
                    size="small"
                    onClick={() => {
                      handleActivatePolygon();
                      setDrawToolsOpen(false);
                    }}
                    sx={{
                      color: 'var(--theme-color)',
                      backgroundColor: isPolygonMode ? 'var(--theme-bg-clr)' : '#fff',
                      border: isPolygonMode ? '2px solid var(--theme-color)' : '1px solid transparent',
                      borderRadius: '6px',
                      '&:hover': { backgroundColor: 'var(--theme-bg-clr)' },
                    }}
                  >
                    <PolylineOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Line" placement="left">
                  <IconButton
                    size="small"
                    onClick={() => {
                      handleActivateLine();
                      setDrawToolsOpen(false);
                    }}
                    sx={{
                      color: 'var(--theme-color)',
                      backgroundColor: isLineMode ? 'var(--theme-bg-clr)' : '#fff',
                      border: isLineMode ? '2px solid var(--theme-color)' : '1px solid transparent',
                      borderRadius: '6px',
                      '&:hover': { backgroundColor: 'var(--theme-bg-clr)' },
                    }}
                  >
                    <TimelineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        )}

        <div className="wizard__header">
          <p id="wizard-contribution-title" className="wizard__header-title">Add a Place Wizard</p>
          <button
            ref={wizardInitialFocusRef}
            type="button"
            className="wizard__header-close"
            onClick={onClose}
            aria-label="Close wizard"
          >
            &times;
          </button>
        </div>

        <WizardProgressBar currentStep={currentStep} onStepClick={goToStep} />

        {schemaStatus === 'loading' && (
          <div className="wizard__schema-state" role="status" aria-live="polite">
            Loading contribution form…
          </div>
        )}
        {(schemaStatus === 'error' || schemaStatus === 'empty') && (
          <div className="wizard__schema-state wizard__schema-state--error" role="alert">
            <span className="wizard__schema-message">
              {schemaStatus === 'empty'
                ? 'No contribution form is configured for this project.'
                : 'Couldn’t load the contribution form.'}
            </span>
            <button
              ref={schemaRetryButtonRef}
              type="button"
              className="wizard__btn-outlined wizard__schema-retry"
              onClick={retryContributionSchema}
            >
              Retry
            </button>
          </div>
        )}

        {renderStep()}

        <ConfirmationDialog
          open={confirmationOpen}
          onClose={handleConfirmationClose}
          title="Submission received"
          message={confirmationMessage}
          actions={[
            {
              label: 'Close',
              element: (
                <button
                  className="wizard__nav-btn wizard__nav-btn--submit"
                  onClick={handleConfirmationClose}
                >
                  Close
                </button>
              ),
            },
          ]}
        />
        </div>
      </Draggable>
    </div>
  );

  return (
    <WizardErrorBoundary>
      <div className="wizard-backdrop" onClick={onClose} />
      {typeof document !== 'undefined' &&
        createPortal(wizardTree, document.body)}
    </WizardErrorBoundary>
  );
};

export default React.memo(WizardContribution);
